import Link from 'found/Link';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';
import { Input } from 'reactstrap';

import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import getFullAddressString from 'src/util/address';
import logger from 'src/util/logger';

import getFilteredPropertiesAdmin from './helpers/getFilteredPropertiesAdmin';

/**
 * AdminPropertyList component displays a paginated list of properties in the admin interface.
 * It shows property details including status, title, address, identifiers, and community.
 * @param {object} props - Component props
 * @param {object} props.relay - Relay pagination container props
 * @param {object} props.viewer - Viewer object containing properties data
 * @param {object} props.viewer.properties - Paginated properties connection
 * @returns {React.ReactElement} AdminPropertyList component
 */
function AdminPropertyList(props) {
  const loadMore = () => {
    const { relay } = props;

    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    relay.loadMore(
      50,
      (error) => { logger.log(error); },
    );
  };

  if (!props) {
    return <Loading />;
  }

  const { relay, viewer } = props;
  const { properties } = viewer;

  const intl = useIntl();

  // Filter state
  const [filterTerm, setFilterTerm] = useState('');

  // Apply filter to properties
  const filteredProperties = filterTerm
    ? { ...properties, edges: getFilteredPropertiesAdmin(properties.edges, filterTerm) }
    : properties;

  // Page title and meta information
  const pageTitle = intl.formatMessage({
    id: 'admin.admin_property.admin_property_list.page.title',
    defaultMessage: 'Enosi Admin - Properties',
  });

  // Breadcrumb labels
  const breadcrumbAdmin = intl.formatMessage({
    id: 'admin.admin_property.admin_property_list.breadcrumbs.admin.label',
    defaultMessage: 'Admin',
  });
  const breadcrumbProperties = intl.formatMessage({
    id: 'admin.admin_property.admin_property_list.breadcrumbs.properties.label',
    defaultMessage: 'Properties',
  });

  // Table headers
  const tableHeaderStatus = intl.formatMessage({
    id: 'admin.admin_property.admin_property_list.table.head.status.label',
    defaultMessage: 'Status',
  });
  const tableHeaderTitle = intl.formatMessage({
    id: 'admin.admin_property.admin_property_list.table.head.title.label',
    defaultMessage: 'Title',
  });
  const tableHeaderAddress = intl.formatMessage({
    id: 'admin.admin_property.admin_property_list.table.head.address.label',
    defaultMessage: 'Address',
  });
  const tableHeaderId = intl.formatMessage({
    id: 'admin.admin_property.admin_property_list.table.head.id.label',
    defaultMessage: 'ID',
  });
  const tableHeaderExternalIdentifierTitle = intl.formatMessage({
    id: 'admin.admin_property.admin_property_list.table.head.external_identifier.title',
    defaultMessage: 'External identifier',
  });
  const tableHeaderExternalIdentifier = intl.formatMessage({
    id: 'admin.admin_property.admin_property_list.table.head.external_identifier.label',
    defaultMessage: 'Ext ID',
  });
  const tableHeaderCommunity = intl.formatMessage({
    id: 'admin.admin_property.admin_property_list.table.head.community.label',
    defaultMessage: 'Community',
  });

  // Filter input labels
  const filterPlaceholder = intl.formatMessage({
    id: 'admin.admin_property.admin_property_list.filter.placeholder',
    defaultMessage: 'Search properties by title, address, or external identifier...',
  });
  const filterAriaLabel = intl.formatMessage({
    id: 'admin.admin_property.admin_property_list.filter.aria_label',
    defaultMessage: 'Filter properties',
  });

  // Load more button text
  const copyLoadMore = intl.formatMessage({
    id: 'admin.admin_property.admin_property_list.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const copyNoMore = intl.formatMessage({
    id: 'admin.admin_property.admin_property_list.load_more_button.state_no_more.label',
    defaultMessage: 'No more properties to load',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbProperties },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">{breadcrumbProperties}</h1>
      </div>

      <div className="mb-3">
        <Input
          type="search"
          placeholder={filterPlaceholder}
          value={filterTerm}
          onChange={(e) => setFilterTerm(e.target.value)}
          aria-label={filterAriaLabel}
        />
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{tableHeaderStatus}</th>
                <th scope="col">{tableHeaderTitle}</th>
                <th scope="col">{tableHeaderAddress}</th>
                <th scope="col">{tableHeaderId}</th>
                <th scope="col">
                  <abbr title={tableHeaderExternalIdentifierTitle}>
                    {tableHeaderExternalIdentifier}
                  </abbr>
                </th>
                <th scope="col">{tableHeaderCommunity}</th>
              </tr>
            </thead>
            <tbody>
              {
                filteredProperties && filteredProperties.edges
                  && filteredProperties.edges.map((edge) => {
                    const { node } = edge;
                    const {
                      uuid, title, address, active, externalIdentifier, community,
                    } = node;
                    const addr = getFullAddressString(address);
                    const { uuid: communityId, title: communityTitle } = community;

                    return (
                      <tr key={`admin-property-${uuid}`}>
                        <td>
                          <BadgeActiveTimeRange active={active} />
                        </td>
                        <th scope="row" colSpan={2}>
                          <Link to={`/admin/properties/${uuid}`}>
                            {title}
                          </Link>
                          {addr && (
                          <>
                            <br />
                            <div>{addr}</div>
                          </>
                          )}
                        </th>
                        <td>
                          <UUID uuid={uuid} />
                        </td>
                        <td>
                          {externalIdentifier}
                        </td>
                        <td>
                          <Link to={`/admin/communities/${communityId}`}>
                            {communityTitle}
                          </Link>
                          <br />
                          <UUID uuid={communityId} />
                        </td>
                      </tr>
                    );
                  })
              }
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={relay.hasMore()}
          />
        </div>
      </div>
    </>
  );
}

AdminPropertyList.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

AdminPropertyList.defaultProps = {
  viewer: null,
};

export default createPaginationContainer(
  AdminPropertyList,
  {
    viewer: graphql`
      fragment AdminPropertyList_viewer on Viewer {
        id
        viewerUser {
          email
          givenName
          familyName
        }
        properties(
          first: $count
          after: $cursor
        ) @connection(key: "AdminPropertyList_properties") {
          edges {
            cursor
            node {
              uuid
              title
              externalIdentifier
              address {
                line1
                line2
                city
                state
                postcode
              }
              active {
                start
                finish
              }
              community {
                uuid
                title
              }
            }
          }
          count
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.viewer.properties;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }) {
      return {
        count,
        cursor,
      };
    },
    query: graphql`
    # Pagination query to be fetched upon calling 'loadMore'.
    # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
    query AdminPropertyList_Pagination_Query(
      $count: Int!
      $cursor: String
    ) {
      viewer {
        ...AdminPropertyList_viewer
      }
    }
  `,
  },
);
